import type { Event } from 'models';
import { useMemo } from 'react';
import { usePermissions } from 'permissions';
import { Form, FormActions, FormLayout, Modal } from 'ui';
import type { SlotStart } from 'features/calendar';
import {
  AttachmentsFormContent,
  type AttachmentsFormContentValues,
  ChannelFormContent,
  DescriptionImageFormContent,
  DetailFormContent,
  DurationFormContent,
  type ExtendedChannelFormContentValues,
  type ExtendedDescriptionImageFormContentValues,
  type ExtendedDetailFormContentValues,
  type ExtendedDurationFormValues,
} from '../../components';
import { useUploadEventAttachments, useUploadEventCoverPhoto } from '../../hooks';
import { type ExtendedInvitationsFormValues, InvitationsFormContent } from '../../invitations-form-content';
import { useCreateEventMutation } from '../../queries';
import { useCreateFormRequestData, useGetInitialValues } from './hooks';

export type CreateEventFormValues = AttachmentsFormContentValues &
  ExtendedChannelFormContentValues &
  ExtendedDescriptionImageFormContentValues &
  ExtendedDetailFormContentValues &
  ExtendedDurationFormValues &
  ExtendedInvitationsFormValues;

type P = {
  onCancel: () => void;
  onSuccess: (eventData: Event) => void;
  slotStart?: SlotStart;
};

const CreateEventForm = ({ onCancel, onSuccess, slotStart }: P) => {
  const { isPending, mutate: createEvent } = useCreateEventMutation();
  const { canManageEventChannels } = usePermissions();
  const { isUploadingAttachments, uploadEventAttachments } = useUploadEventAttachments();
  const { isUploadingCoverPhoto, uploadEventCoverPhoto } = useUploadEventCoverPhoto();
  const initialValues = useGetInitialValues(slotStart);
  const createFormRequestData = useCreateFormRequestData();

  const submit = (formValues: CreateEventFormValues) => {
    createEvent(createFormRequestData(formValues), {
      async onSuccess(eventData) {
        await uploadEventCoverPhoto({ eventId: eventData.id, heroImage: formValues.heroImage });
        await uploadEventAttachments({ attachments: formValues.attachments, eventId: eventData.id });
        onSuccess(eventData);
      },
    });
  };

  const isLoading = useMemo(
    () => isPending || isUploadingAttachments || isUploadingCoverPhoto,
    [isPending, isUploadingAttachments, isUploadingCoverPhoto],
  );

  return (
    <Form<CreateEventFormValues> initialValues={initialValues} onFinish={submit}>
      <FormLayout>
        <DetailFormContent<CreateEventFormValues> mode="create" />
        <DurationFormContent<CreateEventFormValues> />
        <InvitationsFormContent<CreateEventFormValues> />
        <DescriptionImageFormContent<CreateEventFormValues> />
        {canManageEventChannels({}) ? <ChannelFormContent<CreateEventFormValues> /> : null}
        <AttachmentsFormContent<CreateEventFormValues> />
      </FormLayout>
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            children: 'Cancel',
            onClick: onCancel,
          }}
          submit={{
            children: 'Create',
            loading: isLoading,
          }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default CreateEventForm;
